import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../FullResultsPage/FullResultsPage.module.css";

import { appNotification, BlueButton } from "@react-solutions-ui/findmore-ui";
import SearchIcon from "../../../../assets/icons/searchIcon.svg";
import FilterIcon from "../../../../assets/icons/filterIcon.svg";
import ViewIcon from "../../../../assets/icons/viewIcon.svg";
import EditIcon from "../../../../assets/icons/editIcon.svg";
import DeleteIcon from "../../../../assets/icons/deleteIcon.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/closeIcon.svg";
import AddNoteIcon from "../../../../assets/icons/addNoteIconWhite.svg";
import ArrowRightIcon from "../../../../assets/icons/arrowTeamIcon.svg";
import {
  TableComponent,
  ActionsContainer,
  Modal,
} from "@react-solutions-ui/findmore-ui";
import FiltersPanel from "../../../../components/ui/FiltersPanel/FiltersPanel";
import AddNotePanel from "../../../../components/ui/NotePanel/AddNotePanel";
import BreadcrumbDropdown from "../../../reports/components/Banner/BreadcrumbDropdown";
import { Breadcrumb } from "antd";
import { FormOutlined } from "@ant-design/icons";

import DeleteNoteModal from "../../../../components/ui/NotePanel/DeleteNoteModal/DeleteNoteModal";
import EditNotePanel from "../../../../components/ui/NotePanel/EditNotePanel";
import ViewNotePanel from "../../../../components/ui/NotePanel/ViewNotePanel";
import { updateNotes } from "../../hooks/useProfile";

type NoteHistory = {
  id: string;
  createdBy: string;
  createdOn: string;
  categoryName: string;
  title: string;
  note: string;
};

type NoteFilters = {
  author: string;
  category: string;
  year: string;
  startDate: string | null;
  endDate: string | null;
};

type LocationState = {
  notes: NoteHistory[];
  breadcrumbLabel: string;
  employeeName: string;
  employeeArea: string;
  photo: string;
  employeeId: string;
};

export default function FullNotesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | undefined;

  const [isMobile, setIsMobile] = useState(false);
  const [successModal, setSuccessModal] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filtersActive, setFiltersActive] = useState(false);
  const [activeFilters, setActiveFilters] = useState<NoteFilters>({
    author: "Todos",
    category: "Todas",
    year: "Todos",
    startDate: null,
    endDate: null,
  });
  const [showNotePanel, setShowNotePanel] = useState(false);

  const [selectedNote, setSelectedNote] = useState<NoteHistory | null>(null);
  const [viewNoteOpen, setViewNoteOpen] = useState(false);
  const [editNoteOpen, setEditNoteOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notesHistory, setNotesHistory] = useState<NoteHistory[]>([]);

  useEffect(() => {
    setNotesHistory(notesHistory);
  }, [notesHistory]);

  const authors = useMemo(() => {
    if (!state?.notes) return [];
    return Array.from(
      new Set(state.notes.map((n) => n.createdBy).filter(Boolean))
    );
  }, [state?.notes]);

  const categories = useMemo(() => {
    if (!state?.notes) return [];
    return Array.from(
      new Set(state.notes.map((n) => n.categoryName).filter(Boolean))
    );
  }, [state?.notes]);

  const years: string[] = useMemo(() => {
    if (!state?.notes) return [];
    return Array.from(
      new Set(
        state.notes
          .map((n) =>
            n.createdOn ? new Date(n.createdOn).getFullYear().toString() : null
          )
          .filter((y): y is string => y !== null && y !== undefined)
      )
    );
  }, [state?.notes]);

  const dates = useMemo(() => {
    if (!state?.notes) return [];
    return Array.from(
      new Set(state.notes.map((n) => n.createdOn).filter(Boolean))
    );
  }, [state?.notes]);

  const openNotificationWithIcon = (
    _type: "success",
    message: string,
    description: string
  ) => {
    appNotification.success({
      message: (
        <>
          <strong>{message}</strong>
          <br />
          {description}
        </>
      ),
      duration: 3,
    });
  };

  const notesColumns = [
    {
      title: "Autor",
      dataIndex: "createdBy",
      key: "createdBy",
      sorter: (a: NoteHistory, b: NoteHistory) =>
        a.createdBy.localeCompare(b.createdBy),
      onCell: () => ({ "data-label": "Autor" } as any),
      render: (text: string, record: NoteHistory) => (
        <div className={styles.rowWithActions}>
          <span className={styles.authorText}>{text}</span>
          <ActionsContainer mobile={true}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedNote(record);
                setViewNoteOpen(true);
              }}
            >
              <img
                src={ViewIcon}
                alt="ver"
                className={styles.mobileActionIcon}
              />
            </a>

            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedNote(record);
                setEditNoteOpen(true);
              }}
            >
              <img
                src={EditIcon}
                alt="editar"
                className={styles.mobileActionIcon}
              />
            </a>

            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedNote(record);
                setDeleteModalOpen(true);
              }}
            >
              <img
                src={DeleteIcon}
                alt="eliminar"
                className={styles.mobileActionIcon}
              />
            </a>
          </ActionsContainer>
        </div>
      ),
    },
    {
      title: "Data",
      dataIndex: "createdOn",
      key: "createdOn",
      sorter: (a: NoteHistory, b: NoteHistory) =>
        new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
      onCell: () => ({ "data-label": "Data" } as any),
      render: (text: string) =>
        text ? new Date(text).toLocaleDateString("pt-PT") : "-",
    },
    {
      title: "Categoria",
      dataIndex: "categoryName",
      key: "categoryName",
      onCell: () => ({ "data-label": "Categoria" } as any),
      render: (value: string) =>
        value ? (
          <span
            className={styles.noteCategoryTag}
            data-category={value.toUpperCase()}
          >
            {value}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Assunto",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      onCell: () => ({ "data-label": "Assunto" } as any),
    },
    {
      title: "Mensagem",
      dataIndex: "note",
      key: "note",
      ellipsis: true,
      onCell: () => ({ "data-label": "Mensagem" } as any),
    },
    {
      title: "Ações",
      key: "actions",
      className: "hide-on-mobile",
      onCell: () => ({ "data-label": "Ações" } as any),
      render: (_: any, record: NoteHistory) => (
        <ActionsContainer>
          <a
            href="#"
            className={styles.viewNote}
            onClick={(e) => {
              e.preventDefault();
              setSelectedNote(record);
              setViewNoteOpen(true);
            }}
          >
            Ver nota
          </a>
          <a
            href="#"
            className={styles.editNote}
            style={{ marginLeft: 12 }}
            onClick={(e) => {
              e.preventDefault();
              setSelectedNote(record);
              setEditNoteOpen(true);
            }}
          >
            Editar
          </a>
          <a
            href="#"
            className={styles.delete}
            style={{ marginLeft: 12 }}
            onClick={(e) => {
              e.preventDefault();
              setSelectedNote(record);
              setDeleteModalOpen(true);
            }}
          >
            Eliminar
          </a>
        </ActionsContainer>
      ),
    },
  ];

  const filteredNotes = useMemo(() => {
    if (!state?.notes) return [];

    const term = searchTerm.toLowerCase();

    return state.notes.filter((note) => {
      const matchesSearch =
        note.createdBy?.toLowerCase().includes(term) ||
        note.title?.toLowerCase().includes(term) ||
        note.note?.toLowerCase().includes(term);

      const matchesAuthor =
        activeFilters.author === "Todos" ||
        note.createdBy === activeFilters.author;

      const matchesCategory =
        activeFilters.category === "Todas" ||
        note.categoryName === activeFilters.category;

      const matchesYear =
        activeFilters.year === "Todos" ||
        (note.createdOn &&
          new Date(note.createdOn).getFullYear() ===
            Number(activeFilters.year));

      let matchesDate = true;
      if (activeFilters.startDate) {
        matchesDate =
          new Date(note.createdOn).setHours(0, 0, 0, 0) >=
          new Date(activeFilters.startDate).setHours(0, 0, 0, 0);
      }
      if (matchesDate && activeFilters.endDate) {
        matchesDate =
          new Date(note.createdOn).setHours(0, 0, 0, 0) <=
          new Date(activeFilters.endDate).setHours(0, 0, 0, 0);
      }

      return (
        matchesSearch &&
        matchesAuthor &&
        matchesCategory &&
        matchesYear &&
        matchesDate
      );
    });
  }, [state?.notes, searchTerm, activeFilters]);

  const hiddenItems = [
    {
      label: state?.breadcrumbLabel ?? "",
      href:
        state?.breadcrumbLabel === "A minha equipa"
          ? "/my-team"
          : "/all-employees",
    },
    {
      label: state?.employeeName ?? "",
      href: `/profile-team/${state?.employeeId}`,
    },
  ];

  const breadcrumbItems = isMobile
    ? [
        {
          title: (
            <BreadcrumbDropdown
              trigger={<span className={styles.ellipsisButton}>...</span>}
              items={hiddenItems}
            />
          ),
        },
        { title: <b>Histórico de Notas</b>, href: "" },
      ]
    : [
        {
          title: (
            <a
              style={{ cursor: "pointer" }}
              onClick={() =>
                navigate(
                  state?.breadcrumbLabel === "A minha equipa"
                    ? "/my-team"
                    : "/all-employees"
                )
              }
            >
              {state?.breadcrumbLabel}
            </a>
          ),
        },
        {
          title: (
            <a
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/profile-team/${state?.employeeId}`)}
            >
              {state?.employeeName}
            </a>
          ),
        },
        {
          title: <b>Histórico de Notas</b>,
        },
      ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!state) {
      navigate("/all-employees");
    }
  }, [state, navigate]);

  if (!state) return null;

  return (
    <>
      <div className={styles.blueProfile}>
        <div className="page-container">
          <Breadcrumb
            className={styles.breadcrumb}
            separator={<img src={ArrowRightIcon} alt="arrow" />}
            items={breadcrumbItems}
          />

          <div className={styles.userInfo}>
            <div className={styles.avatarCircle}>
              {state.photo ? (
                <img
                  src={`data:image/jpeg;base64,${atob(state.photo)}`}
                  alt="Avatar"
                />
              ) : (
                state.employeeName
                  .split(" ")
                  .map((p) => p[0])
                  .join("")
                  .toUpperCase()
              )}
            </div>

            <div className={styles.userText}>
              <h2 className={styles.userName}>{state.employeeName}</h2>
              <p className={styles.areaEmployee}>{state.employeeArea}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={`${styles.profileContainer} page-container`}>
        {successModal.open && (
          <Modal
            title="SUCESSO"
            type="info"
            onCloseModal={() =>
              setSuccessModal({ ...successModal, open: false })
            }
            closeModalLabel="FECHAR"
          >
            {successModal.message}
          </Modal>
        )}

        <div className={styles.profileBox}>
          <div className={styles.notesBox}>
            <div className={styles.textBox}>
              <span className={styles.titleHistory}>HISTÓRICO DE NOTAS</span>
              <span className={styles.smallText}>
                Revê as notas anteriores do colaborador
              </span>
            </div>

            <button
              className={styles.addNoteMobile}
              onClick={() => setShowNotePanel(true)}
            >
              <FormOutlined
                className={styles.addNotesIcon}
                alt="ADICIONAR NOTA"
              />
            </button>

            <button
              className={styles.filterButtonMobile}
              onClick={() => setIsFiltersOpen(true)}
            >
              <img src={FilterIcon} alt="Filtros" />
            </button>
          </div>

          <div className={styles.filterBar}>
            <div className={styles.searchContainer}>
              <input
                type="text"
                placeholder="Procurar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              <img src={SearchIcon} className={styles.searchIcon} />
            </div>

            <button
              className={styles.filterButtonDesktop}
              onClick={() => setIsFiltersOpen(true)}
            >
              <img src={FilterIcon} /> FILTROS
            </button>

            <button
              className={`${styles.clearFilters} ${
                filtersActive ? styles.active : styles.default
              }`}
              disabled={!filtersActive}
              onClick={() => {
                setActiveFilters({
                  author: "Todos",
                  category: "Todas",
                  year: "Todos",
                  startDate: null,
                  endDate: null,
                });
                setFiltersActive(false);
                openNotificationWithIcon(
                  "success",
                  "Filtros limpos",
                  "Filtros limpos com sucesso"
                );
              }}
            >
              <CloseIcon className={styles.closeIcon} /> LIMPAR FILTROS
            </button>

            <div className={styles.addNoteDesktop}>
              <BlueButton
                className={`${styles.addNoteBtn} ${
                  showNotePanel ? styles.active : ""
                }`}
                icon={AddNoteIcon}
                text="ADICIONAR NOTA"
                onClick={() => setShowNotePanel(true)}
              />
            </div>
          </div>

          <TableComponent
            columns={notesColumns}
            data={filteredNotes}
            pageSize={9}
          />
        </div>
      </div>

      {isFiltersOpen && (
        <FiltersPanel
          isOpen={isFiltersOpen}
          activeFilters={activeFilters}
          authors={authors}
          categories={categories}
          years={years}
          dates={dates}
          onClose={() => setIsFiltersOpen(false)}
          onApply={(newFilters) => {
            setActiveFilters(newFilters);
            setFiltersActive(true);
            setIsFiltersOpen(false);
            openNotificationWithIcon(
              "success",
              "Filtros aplicados",
              "Filtros aplicados com sucesso"
            );
          }}
          onClear={() => {
            setActiveFilters({
              author: "Todos",
              category: "Todas",
              year: "Todos",
              startDate: null,
              endDate: null,
            });
            setFiltersActive(false);
            setIsFiltersOpen(false);
            openNotificationWithIcon(
              "success",
              "Filtros limpos",
              "Filtros limpos com sucesso"
            );
          }}
        />
      )}

      {showNotePanel && (
        <AddNotePanel
          employeeId={state.employeeId}
          user={state.employeeName}
          onClose={() => setShowNotePanel(false)}
          onSuccess={(msg: string) => {
            setSuccessModal({
              open: true,
              message: msg,
            });
            setShowNotePanel(false);
          }}
        />
      )}

      {viewNoteOpen && selectedNote && (
        <ViewNotePanel
          note={selectedNote}
          employeeId={state.employeeId}
          user={state.employeeName}
          onClose={() => setViewNoteOpen(false)}
        />
      )}

      {editNoteOpen && selectedNote && (
        <EditNotePanel
          note={selectedNote}
          employeeId={state.employeeId}
          user={state.employeeName}
          onClose={() => setEditNoteOpen(false)}
          onSuccessEdit={async (msg: string) => {
            setSuccessModal({
              open: true,
              message: msg,
            });
            var newdata = await updateNotes(state.employeeId);
            if (newdata) {
              setNotesHistory(newdata.notesHistory);
            }
          }}
        />
      )}

      {deleteModalOpen && selectedNote && (
        <DeleteNoteModal
          noteId={selectedNote.id}
          employeeId={state.employeeId}
          onClose={() => setDeleteModalOpen(false)}
          onSuccess={async (msg) => {
            setSuccessModal({
              open: true,
              message: msg,
            });
            var newdata = await updateNotes(state.employeeId);
            if (newdata) {
              setNotesHistory(newdata.notesHistory);
            }
          }}
        />
      )}
    </>
  );
}
